import { useMemo, useState } from 'react'
import { Button } from '../../components'
import {
  useClientErrorSummary,
  useClientErrors,
  type ClientErrorEntry,
  type ClientErrorKind,
} from '../../lib/api/client-errors'

const KIND_LABELS: Record<ClientErrorKind, string> = {
  render: 'Рендер',
  window: 'Вікно',
  promise: 'Проміс',
  api: 'API',
  network: 'Мережа',
}

const KIND_COLORS: Record<ClientErrorKind, string> = {
  render: 'var(--danger-fg)',
  window: 'var(--danger-fg)',
  promise: 'var(--warning-fg, var(--fg-2))',
  api: 'var(--fg-2)',
  network: 'var(--warning-fg, var(--fg-2))',
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '6px 8px',
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
  fontSize: 13,
}

const cellStyle: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'top' }

const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 12,
  color: 'var(--fg-3)',
}

function stamp(value: string): string {
  return value.replace('T', ' ').slice(0, 19)
}

export function ClientErrorsSection() {
  const [kind, setKind] = useState<ClientErrorKind | ''>('')
  const [fingerprint, setFingerprint] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const filters = useMemo(
    () => ({
      kind: kind || null,
      fingerprint: fingerprint || null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [kind, fingerprint, dateFrom, dateTo],
  )

  const summary = useClientErrorSummary()
  const query = useClientErrors(filters)
  const items = query.data?.pages.flatMap((p) => p.items) ?? []
  const groups = summary.data?.groups ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Помилки інтерфейсу</h2>
      </div>

      <p style={{ margin: 0, color: 'var(--fg-3)', fontSize: 13, lineHeight: 1.5 }}>
        Помилки, які сталися в браузері користувача. Серверні логи їх не бачать: збій рендера не
        доходить до сервера взагалі, а збої входу відбуваються на боці Clerk.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 8,
          padding: 12,
          border: '1px solid var(--border)',
          borderRadius: 6,
        }}
      >
        <FilterField label="Тип">
          <select
            style={inputStyle}
            value={kind}
            onChange={(e) => setKind(e.target.value as ClientErrorKind | '')}
          >
            <option value="">Всі</option>
            {Object.entries(KIND_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Група">
          <input
            style={inputStyle}
            value={fingerprint}
            onChange={(e) => setFingerprint(e.target.value)}
            placeholder="fingerprint"
          />
        </FilterField>
        <FilterField label="Дата з">
          <input
            style={inputStyle}
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label="Дата по">
          <input
            style={inputStyle}
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
      </div>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: 'var(--fg-2)' }}>
          Групи помилок
        </h3>
        {summary.isLoading ? (
          <Empty text="Завантаження…" />
        ) : summary.isError ? (
          <Empty text="Не вдалося завантажити зведення" />
        ) : groups.length === 0 ? (
          <Empty text="Помилок ще не було" />
        ) : (
          <TableShell headers={['Приклад', 'Тип', 'Разів', 'Уперше', 'Востаннє']}>
            {groups.map((g) => (
              <tr
                key={g.fingerprint}
                onClick={() => setFingerprint(g.fingerprint)}
                style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
              >
                <td style={cellStyle}>{g.sample}</td>
                <td style={{ ...cellStyle, color: KIND_COLORS[g.kind], fontWeight: 500 }}>
                  {KIND_LABELS[g.kind] ?? g.kind}
                </td>
                <td style={{ ...cellStyle, fontVariantNumeric: 'tabular-nums' }}>{g.count}</td>
                <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{stamp(g.firstSeen)}</td>
                <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{stamp(g.lastSeen)}</td>
              </tr>
            ))}
          </TableShell>
        )}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: 'var(--fg-2)' }}>Події</h3>
        {query.isLoading ? (
          <Empty text="Завантаження…" />
        ) : query.isError ? (
          <Empty text="Не вдалося завантажити список" />
        ) : items.length === 0 ? (
          <Empty text="Нічого не знайдено" />
        ) : (
          <TableShell headers={['Час', 'Тип', 'Повідомлення', 'Маршрут', 'Користувач']}>
            {items.map((row) => (
              <ErrorRow
                key={row.id}
                row={row}
                expanded={expanded === row.id}
                onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
              />
            ))}
          </TableShell>
        )}
        {query.hasNextPage && (
          <Button
            variant="secondary"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Завантаження…' : 'Завантажити ще'}
          </Button>
        )}
      </section>
    </div>
  )
}

function ErrorRow({
  row,
  expanded,
  onToggle,
}: {
  row: ClientErrorEntry
  expanded: boolean
  onToggle: () => void
}) {
  const where = row.apiPath
    ? `${row.apiPath}${row.apiStatus == null ? '' : ` → ${row.apiStatus}`}`
    : (row.route ?? '—')

  return (
    <>
      <tr onClick={onToggle} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
        <td style={{ ...cellStyle, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {stamp(row.createdAt)}
        </td>
        <td style={{ ...cellStyle, color: KIND_COLORS[row.kind], fontWeight: 500 }}>
          {KIND_LABELS[row.kind] ?? row.kind}
        </td>
        <td style={cellStyle}>{row.message}</td>
        <td style={{ ...cellStyle, ...monoStyle }}>{where}</td>
        <td style={cellStyle}>
          {row.userEmail ?? <span style={{ color: 'var(--fg-4)' }}>анонім</span>}
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: 'var(--bg-2)' }}>
          <td colSpan={5} style={{ padding: '12px 12px 16px' }}>
            <dl style={{ margin: 0, display: 'grid', gap: 4, fontSize: 12 }}>
              <Detail label="Група" value={row.fingerprint} mono />
              <Detail label="Маршрут" value={row.route ?? '—'} mono />
              <Detail label="Бандл" value={row.bundle ?? '—'} mono />
              <Detail label="Сесія" value={row.sessionRef ?? '—'} mono />
              <Detail label="Браузер" value={row.userAgent ?? '—'} />
            </dl>
            {row.stack && (
              <pre
                style={{
                  ...monoStyle,
                  marginTop: 12,
                  marginBottom: 0,
                  padding: 12,
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  background: 'var(--bg-1)',
                  overflowX: 'auto',
                  whiteSpace: 'pre',
                }}
              >
                {row.stack}
              </pre>
            )}
            {row.bundle && (
              <p style={{ ...monoStyle, marginTop: 8, marginBottom: 0 }}>
                {`pnpm --filter @finickly/worker run decode-stack -- ${row.bundle} "<стек>"`}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <dt style={{ color: 'var(--fg-4)', minWidth: 90 }}>{label}</dt>
      <dd style={{ margin: 0, ...(mono ? monoStyle : {}) }}>{value}</dd>
    </div>
  )
}

function TableShell({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--bg-2)' }}>
            {headers.map((label) => (
              <th
                key={label}
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontWeight: 500,
                  color: 'var(--fg-3)',
                }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: 16,
        border: '1px solid var(--border)',
        borderRadius: 6,
        color: 'var(--fg-4)',
        fontSize: 13,
      }}
    >
      {text}
    </div>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 11,
        color: 'var(--fg-4)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      <span>{label}</span>
      {children}
    </label>
  )
}
